import React, { useState, useRef, useEffect } from 'react';
import { XMarkIcon, ArrowDownTrayIcon, ArrowUpTrayIcon, EyeIcon, EyeSlashIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { PontBascule } from '../../types/scaleAgent';
import { Tenant, Site } from '../../services/sites';
import { pontBasculeService } from '../../services/pontBasculeService';
import ConfigFileGenerator from '../../utils/configFileGenerator';

interface AgentConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  pontBascule: PontBascule;
  tenant: Tenant;
  site: Site;
  onConfigUpdate?: (data: any) => void;
}

const AgentConfigModal: React.FC<AgentConfigModalProps> = ({
  isOpen,
  onClose,
  pontBascule,
  tenant,
  site,
  onConfigUpdate
}) => {
  const [apiSecretKey, setApiSecretKey] = useState<string>('');
  const [showKey, setShowKey] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [resetMessage, setResetMessage] = useState('');
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');
  const [uploadMessage, setUploadMessage] = useState('');
  const [uploadedFile, setUploadedFile] = useState<any>(null);

  // Référence pour l'input de fichier
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Charger la clé secrète d'API seulement si elle n'existe pas déjà
  useEffect(() => {
    if (isOpen && pontBascule.id && !apiSecretKey) {
      loadApiSecretKey();
    }
  }, [isOpen, pontBascule.id, apiSecretKey]);

  const loadApiSecretKey = async () => {
    try {
      console.log('🔍 Chargement de la clé secrète d\'API...');
      
      // Vérifier d'abord si on a déjà une clé en localStorage
      const savedKey = localStorage.getItem(`api_secret_key_${pontBascule.id}`);
      if (savedKey) {
        console.log('🔐 Clé trouvée en localStorage:', savedKey);
        setApiSecretKey(savedKey);
        return;
      }
      
      // Si pas de clé sauvegardée, la récupérer depuis le backend
      const pontBasculeData = await pontBasculeService.getPontBasculeById(pontBascule.id);
      console.log('🔍 Données pont bascule récupérées:', pontBasculeData);
      
      if (pontBasculeData && pontBasculeData.apiSecretKey) {
        console.log('🔐 Clé récupérée depuis le backend:', pontBasculeData.apiSecretKey);
        setApiSecretKey(pontBasculeData.apiSecretKey);
        
        // Sauvegarder la clé en localStorage
        localStorage.setItem(`api_secret_key_${pontBascule.id}`, pontBasculeData.apiSecretKey);
        console.log('💾 Clé sauvegardée en localStorage');
      } else {
        console.log('⚠️ Aucune clé secrète d\'API trouvée, génération d\'une nouvelle clé');
        // Si pas de clé, en générer une nouvelle
        await resetApiSecretKey();
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement de la clé:', error);
      // En cas d'erreur, générer une nouvelle clé
      await resetApiSecretKey();
    }
  };

  const resetApiSecretKey = async () => {
    setIsResetting(true);
    setResetMessage('');
    
    try {
      console.log('🔄 Début de la réinitialisation de la clé secrète...');
      const response = await pontBasculeService.resetApiSecretKey(pontBascule.id);
      console.log('✅ Réponse de réinitialisation:', response);
      console.log('🔐 Nouvelle clé reçue:', response.new_key);
      console.log('🔐 Longueur nouvelle clé:', response.new_key.length);
      
      // Mettre à jour l'état local
      setApiSecretKey(response.new_key);
      console.log('🔐 État local mis à jour avec la nouvelle clé');
      
      // Sauvegarder la nouvelle clé en localStorage
      localStorage.setItem(`api_secret_key_${pontBascule.id}`, response.new_key);
      console.log('💾 Nouvelle clé sauvegardée en localStorage');
      
      setResetMessage(response.warning);
      
      // Afficher un message de succès temporaire
      setTimeout(() => {
        setResetMessage('');
      }, 5000);
      
    } catch (error: any) {
      console.error('❌ Erreur lors de la réinitialisation:', error);
      setResetMessage(`Erreur: ${error.message || 'Erreur inconnue'}`);
      
      // Effacer le message d'erreur après 5 secondes
      setTimeout(() => {
        setResetMessage('');
      }, 5000);
    } finally {
      setIsResetting(false);
    }
  };

  const handleDownloadConfig = () => {
    try {
      console.log('🔍 Pont Bascule:', pontBascule);
      console.log('🔍 Tenant:', tenant);
      console.log('🔍 Site:', site);
      console.log('🔐 Clé secrète d\'API actuelle:', apiSecretKey);
      console.log('🔐 Format de la clé - Longueur:', apiSecretKey.length);
      
      // Utiliser directement les données du pont bascule si tenant/site ne sont pas disponibles
      const effectiveTenant = tenant || {
        id: pontBascule.tenantId || 'N/A',
        name: 'N/A',
        code: 'N/A'
      };
      
      const effectiveSite = site || {
        id: pontBascule.siteId || 'N/A',
        name: 'N/A',
        code: 'N/A'
      };

      const configData = ConfigFileGenerator.prepareConfigData(pontBascule, effectiveTenant, effectiveSite);
      console.log('🔍 Config Data préparée:', configData);
      
      const filename = `config_agent_${pontBascule.code}_${new Date().toISOString().split('T')[0]}.json`;
      console.log('🔍 Nom du fichier:', filename);
      
      ConfigFileGenerator.downloadConfigFile(configData, filename);
      console.log('✅ Fichier téléchargé avec succès');
    } catch (error: any) {
      console.error('❌ Erreur lors du téléchargement:', error);
      alert(`Erreur lors de la génération du fichier de configuration: ${error.message || 'Erreur inconnue'}`);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadStatus('uploading');
    setUploadMessage('Traitement du fichier...');

    try {
      const content = await file.text();
      const parsedData = ConfigFileGenerator.parseAgentFile(content);

      if (parsedData) {
        setUploadStatus('success');
        setUploadedFile(parsedData);
        
        // Détecter le type de fichier et afficher le message approprié
        if ('networkInfo' in parsedData) {
          // C'est un fichier de retour d'état
          setUploadMessage('Fichier de retour d\'état traité avec succès ! Prêt à être enregistré.');
        } else {
          // C'est un fichier de configuration
          setUploadMessage('Fichier de configuration d\'agent traité avec succès ! Prêt à être enregistré.');
          console.log('📋 Configuration reçue:', parsedData);
        }
      } else {
        setUploadStatus('error');
        setUploadMessage('Format de fichier invalide ou corrompu');
      }
    } catch (error: any) {
      setUploadStatus('error');
      // Afficher le message d'erreur spécifique
      const errorMessage = error?.message || 'Erreur lors du traitement du fichier';
      setUploadMessage(`Erreur: ${errorMessage}`);
      console.error('Erreur upload:', error);
    }
  };

  const triggerFileUpload = () => {
    fileInputRef.current?.click();
  };

  const downloadExampleReturnFile = () => {
    try {
      const exampleContent = ConfigFileGenerator.generateExampleReturnFile();
      const blob = new Blob([exampleContent], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = 'exemple_fichier_retour_agent.json';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      console.log('✅ Exemple de fichier de retour téléchargé');
    } catch (error) {
      console.error('❌ Erreur lors du téléchargement de l\'exemple:', error);
    }
  };

  const downloadExampleConfigFile = () => {
    try {
      // Charger le fichier d'exemple depuis le dossier public
      fetch('/exemple_fichier_configuration_agent.json')
        .then(response => response.text())
        .then(content => {
          const blob = new Blob([content], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          
          const a = document.createElement('a');
          a.href = url;
          a.download = 'exemple_fichier_configuration_agent.json';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          
          console.log('✅ Exemple de fichier de configuration téléchargé');
        })
        .catch(error => {
          console.error('❌ Erreur lors du téléchargement de l\'exemple de configuration:', error);
        });
    } catch (error) {
      console.error('❌ Erreur lors du téléchargement de l\'exemple:', error);
    }
  };

  const handleSaveReturn = async () => {
    if (!uploadedFile) return;
    
    try {
      setUploadStatus('uploading');
      setUploadMessage('Enregistrement en cours...');
      
      // Appeler le callback pour mettre à jour la configuration
      if (onConfigUpdate) {
        onConfigUpdate(uploadedFile);
      }
      
      setUploadStatus('success');
      
      // Afficher un message approprié selon le type de fichier
      if ('networkInfo' in uploadedFile) {
        setUploadMessage('Retour d\'état enregistré avec succès !');
      } else {
        setUploadMessage('Configuration d\'agent traitée avec succès !');
      }
      
      // Fermer le modal après un délai
      setTimeout(() => {
        onClose();
      }, 2000);
      
    } catch (error: any) {
      setUploadStatus('error');
      const errorMessage = error?.message || 'Erreur lors de l\'enregistrement';
      setUploadMessage(`Erreur: ${errorMessage}`);
      console.error('Erreur enregistrement:', error);
    }
  };

  const handleResetUpload = () => {
    setUploadedFile(null);
    setUploadStatus('idle');
    setUploadMessage('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            Configuration Agent Local - {pontBascule.name}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Contenu */}
        <div className="space-y-6">
          {/* Section Clé secrète d'API */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900 mb-3">
              Clé secrète d'API
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Cette clé secrète est utilisée pour authentifier les données envoyées par l'agent local. 
              Elle est stockée en base de données et sauvegardée localement pour éviter les rechargements.
            </p>
            
            <div className="flex items-center space-x-3 mb-4">
              <div className="flex-1">
                <div className="relative">
                  <input
                    type={showKey ? 'text' : 'password'}
                    value={apiSecretKey}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 font-mono text-sm"
                    placeholder="Clé secrète d'API..."
                  />
                  <button
                    type="button"
                    onClick={() => setShowKey(!showKey)}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showKey ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              
              <button
                onClick={resetApiSecretKey}
                disabled={isResetting}
                className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <ArrowPathIcon className={`h-4 w-4 ${isResetting ? 'animate-spin' : ''}`} />
                <span>Reset Clé</span>
              </button>
            </div>

            {/* Message de réinitialisation */}
            {resetMessage && (
              <div className="bg-orange-50 border border-orange-200 rounded-md p-3">
                <p className="text-sm text-orange-800">{resetMessage}</p>
              </div>
            )}
          </div>

          {/* Section Téléchargement */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900 mb-3">
              Télécharger la configuration
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Téléchargez le fichier de configuration JSON en clair pour l'agent local. 
              Ce fichier contient toutes les informations nécessaires pour configurer l'agent, 
              y compris la clé secrète d'API pour l'authentification.
            </p>
            
            <button
              onClick={handleDownloadConfig}
              disabled={!apiSecretKey}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              <ArrowDownTrayIcon className="h-5 w-5" />
              <span>Télécharger Configuration</span>
            </button>
          </div>

          {/* Section Upload */}
          <div className="bg-green-50 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900 mb-3">
              Upload et Enregistrement des Fichiers
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Le système accepte deux types de fichiers JSON :<br/>
              • <strong>Configuration d'agent</strong> : Fichier envoyé par le système à l'agent<br/>
              • <strong>Retour d'état</strong> : Fichier de statut envoyé par l'agent au système<br/>
              <br/>
              Téléchargez l'exemple approprié selon vos besoins, uploadez votre fichier, puis enregistrez le retour.
            </p>
            
            {!uploadedFile ? (
              // Affichage initial avec boutons d'exemple
              <div className="space-y-3 mb-4">
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={downloadExampleConfigFile}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center justify-center space-x-2"
                  >
                    <ArrowDownTrayIcon className="h-5 w-5" />
                    <span>Exemple Configuration</span>
                  </button>
                  
                  <button
                    onClick={downloadExampleReturnFile}
                    className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 flex items-center justify-center space-x-2"
                  >
                    <ArrowDownTrayIcon className="h-5 w-5" />
                    <span>Exemple Retour d'État</span>
                  </button>
                </div>
                
                <button
                  onClick={triggerFileUpload}
                  className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center justify-center space-x-2"
                >
                  <ArrowUpTrayIcon className="h-5 w-5" />
                  <span>Sélectionner Fichier JSON</span>
                </button>
              </div>
            ) : (
              // Affichage après upload avec boutons d'action
              <div className="space-y-3 mb-4">
                <div className="bg-green-50 border border-green-200 rounded-md p-3">
                  <p className="text-sm text-green-800 font-medium">
                    Fichier chargé avec succès !
                  </p>
                  <p className="text-xs text-green-600 mt-1">
                    Type: {('networkInfo' in uploadedFile) ? 'Retour d\'état' : 'Configuration d\'agent'}
                  </p>
                </div>
                
                <div className="flex gap-3">
                                     <button
                     onClick={handleSaveReturn}
                     className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center justify-center space-x-2"
                   >
                     <ArrowDownTrayIcon className="h-5 w-5" />
                     <span>
                       {('networkInfo' in uploadedFile) ? 'Enregistrer le Retour' : 'Traiter la Configuration'}
                     </span>
                   </button>
                  
                  <button
                    onClick={handleResetUpload}
                    className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 flex items-center justify-center space-x-2"
                  >
                    <ArrowPathIcon className="h-5 w-5" />
                    <span>Nouveau Fichier</span>
                  </button>
                </div>
              </div>
            )}
            
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={handleFileUpload}
              className="hidden"
            />

            {/* Status de l'upload */}
            {uploadStatus !== 'idle' && (
              <div className={`mt-3 p-3 rounded-md ${
                uploadStatus === 'success' ? 'bg-green-50 border border-green-200' :
                uploadStatus === 'error' ? 'bg-red-50 border border-red-200' :
                'bg-blue-50 border border-blue-200'
              }`}>
                <p className={`text-sm ${
                  uploadStatus === 'success' ? 'text-green-800' :
                  uploadStatus === 'error' ? 'text-red-800' :
                  'text-blue-800'
                }`}>
                  {uploadMessage}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default AgentConfigModal;
